import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RESULTADOS, CAUSAS_FALSA, RESOLUCIONES_TECNICAS } from "@/lib/alarmMeta";

// v2.9.11 (Operator Authority) — terminales Día 1 seleccionables en CIERRE DIRECTO (Caso B: sin
// gestión previa registrada). Reutiliza terminales existentes + las dos nuevas honestas.
const DIA1_TERMINALES = [
  { v: "verificado_directamente", l: "Verifiqué el estado de la persona (directo)" },
  { v: "verificado_contacto_autorizado", l: "Un contacto autorizado confirmó su estado" },
  { v: "falsa_alarma", l: "Falsa alarma" },
  { v: "resuelto_otra_via", l: "Resuelto por otra vía" },
  { v: "no_verificado_sin_gestion", l: "Cerrado sin verificación (sin gestión registrada)" },
];
// INSTITUCIONAL: mismos códigos terminales, lenguaje del centro. No se pide al personal concluir
// que el sensor falló: "sin hallazgo al verificar" es factual. La familia no es el eje del cierre.
const DIA1_TERMINALES_INST = [
  { v: "verificado_directamente", l: "Atención verificada por el personal del centro" },
  { v: "falsa_alarma", l: "Sin hallazgo al verificar" },
  { v: "resuelto_otra_via", l: "Resuelto por otra vía interna del centro" },
  { v: "no_verificado_sin_gestion", l: "Cerrado sin verificación (sin gestión registrada)" },
];
// v2.13.1 — RECORDATORIO PROGRAMADO: resoluciones propias. «Recordatorio comunicado» NO afirma
// medicamento tomado, dosis cumplida, tratamiento cumplido ni adherencia.
const RECORDATORIO_TERMINALES = [
  { v: "recordatorio_comunicado", l: "Recordatorio comunicado" },
  { v: "sin_contacto", l: "Sin contacto" },
];
// Terminales cuyo único descriptor de la acción/decisión es la nota -> nota SIEMPRE obligatoria.
const NOTA_OBLIG_TERMINALES = new Set([
  "resuelto_otra_via", "no_verificado_sin_gestion", "no_verificado_accion_final_cumplida",
]);

export default function CloseAlarmModal({
  open, onOpenChange, onConfirm, submitting, initialNota = "",
  dia1Mode = false, dia1Clasificacion = "", dia1Label = "",
  dia1Selectable = false, notaObligatoria = false,
  tecnicoMode = false, resolucionesTecnicas = null, instMode = false, recordatorioMode = false,
  avisoNuevoEvento = null, onVolverAlCaso = null, notasPrevias = [],
}) {
  const [clasificacion, setClasificacion] = useState("");
  const [causa, setCausa] = useState("");
  const [nota, setNota] = useState("");
  const [error, setError] = useState("");

  // ATN-01 · M5 — la nota se carga AL ABRIR. Un refresco del caso (nuevo `initialNota`) ya no
  // sobrescribe lo que la persona está escribiendo.
  useEffect(() => { if (open) setNota(initialNota || ""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // ATN-01 · M3.4 — la preselección del recordatorio llega del caso: el modal la REFLEJA sin pedir
  // que se vuelva a elegir, y sigue siendo editable por el humano.
  useEffect(() => {
    if (!open) return;
    setClasificacion((prev) => (prev ? prev : (dia1Selectable ? (dia1Clasificacion || "") : "")));
  }, [open, dia1Selectable, dia1Clasificacion]);

  const reset = () => { setClasificacion(""); setCausa(""); setNota(""); setError(""); };
  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  // TECH-001: catálogo técnico autoritativo del backend; el espejo local sólo es respaldo de lectura.
  const tecOpciones = (resolucionesTecnicas && resolucionesTecnicas.length ? resolucionesTecnicas : RESOLUCIONES_TECNICAS);
  const tecSel = tecnicoMode ? tecOpciones.find((r) => r.codigo === clasificacion) || null : null;

  // Clasificación efectiva: precargada (Caso A) o seleccionada (Caso B / legacy).
  const precargado = dia1Mode && !dia1Selectable;
  const clsEfectiva = precargado ? dia1Clasificacion : clasificacion;
  const causaRequerida = !tecnicoMode && clsEfectiva === "falsa_alarma";
  const notaEfectivaOblig = tecnicoMode || notaObligatoria || (dia1Selectable && NOTA_OBLIG_TERMINALES.has(clsEfectiva));

  const submit = () => {
    if (!clsEfectiva) {
      setError(tecnicoMode ? "Selecciona la clasificación técnica." : "Selecciona una resolución para cerrar el caso.");
      return;
    }
    if (causaRequerida && !causa) {
      setError("Una falsa alarma requiere indicar la causa presunta.");
      return;
    }
    if (notaEfectivaOblig && !nota.trim()) {
      setError(tecnicoMode ? "Escribe la nota factual de lo ocurrido (obligatoria)."
        : "Este cierre requiere una explicación factual (nota obligatoria).");
      return;
    }
    if (tecnicoMode) {
      onConfirm({ clasificacion: clsEfectiva, nota_cierre: nota, terminal: !!tecSel?.terminal });
      return;
    }
    onConfirm({
      clasificacion: clsEfectiva,
      causa_falsa: clsEfectiva === "falsa_alarma" ? causa : null,
      nota_cierre: nota,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white dark:bg-slate-800 dark:border-slate-700" data-testid="close-alarm-modal">
        <DialogHeader>
          <DialogTitle className="text-[#012472] dark:text-slate-100">
            {tecnicoMode ? "Registrar resolución técnica" : "Cerrar caso"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {tecnicoMode
              ? "Clasifica la incidencia técnica y escribe la nota factual. Sólo Restablecido y Equipo reemplazado cierran la incidencia."
              : "Selecciona la resolución del caso y, si corresponde, escribe una explicación factual."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* OPS-01 §14E — advertencia informativa: llegó un evento nuevo posterior a la última
              verificación efectiva. NO bloquea el cierre ni introduce un 409 nuevo. */}
          {avisoNuevoEvento && (
            <div data-testid="close-aviso-nuevo-evento" className="rounded-lg border-2 border-[#FE6A55] bg-[#FE6A55]/10 p-3">
              <p className="text-sm font-bold text-[#B93B29] dark:text-[#FE6A55]">
                Se detectó un nuevo evento después de la última verificación{avisoNuevoEvento.hora ? ` (${avisoNuevoEvento.hora})` : ""}.
              </p>
              <p className="mt-1 text-xs text-[#B93B29] dark:text-[#FE6A55]">Puedes volver al caso para verificar de nuevo, o continuar con el cierre si ya lo resolviste.</p>
              {onVolverAlCaso && (
                <Button data-testid="close-volver-al-caso" variant="outline" onClick={() => { reset(); onVolverAlCaso(); }}
                  className="mt-2 border-[#FE6A55] text-[#B93B29] dark:text-[#FE6A55]">Volver al caso</Button>
              )}
            </div>
          )}

          {tecnicoMode ? (
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Clasificación técnica (obligatoria)
              </label>
              <Select value={clasificacion} onValueChange={(v) => { setClasificacion(v); setError(""); }}>
                <SelectTrigger data-testid="select-resolucion-tecnica" className="mt-1.5 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecciona la clasificación técnica…" />
                </SelectTrigger>
                <SelectContent>
                  {tecOpciones.map((r) => (
                    <SelectItem key={r.codigo} value={r.codigo} data-testid={`tec-resolucion-${r.codigo}`}>
                      {r.etiqueta}{r.terminal ? "" : " · seguimiento"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tecSel && (
                <p data-testid="tec-semantica" className={`mt-1.5 text-xs font-semibold ${tecSel.terminal ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
                  {tecSel.terminal
                    ? "Resolución terminal: la incidencia se cierra con esta clasificación."
                    : "Seguimiento: la incidencia SIGUE ABIERTA y continúa en la cola hasta que se restablezca o se reemplace el equipo."}
                </p>
              )}
            </div>
          ) : precargado ? (
            <div data-testid="close-dia1-clasificacion" className="rounded-lg border border-[#01CBE3]/40 bg-[#01CBE3]/5 dark:bg-[#01CBE3]/10 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Resultado del caso</p>
              <p className="mt-1 text-sm font-bold text-[#012472] dark:text-slate-100">{dia1Label}</p>
            </div>
          ) : dia1Selectable ? (
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Resolución (obligatoria)
              </label>
              <Select value={clasificacion} onValueChange={(v) => { setClasificacion(v); setCausa(""); setError(""); }}>
                <SelectTrigger data-testid="select-clasificacion-dia1" className="mt-1.5 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecciona cómo se resolvió…" />
                </SelectTrigger>
                <SelectContent>
                  {(recordatorioMode ? RECORDATORIO_TERMINALES : instMode ? DIA1_TERMINALES_INST : DIA1_TERMINALES).map((r) => (
                    <SelectItem key={r.v} value={r.v} data-testid={`dia1-terminal-${r.v}`}>
                      {r.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                {recordatorioMode
                  ? "Galarya sólo confirma que COMUNICÓ el recordatorio: no afirma que el medicamento se tomó ni interpreta adherencia."
                  : instMode
                  ? "La consola registra la decisión del centro; no la juzga. Explica qué verificó el personal y qué quedó documentado."
                  : "La consola registra tu decisión; no la juzga. Explica cómo determinaste que el caso podía cerrarse."}
              </p>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Clasificación (obligatoria)
              </label>
              <Select value={clasificacion} onValueChange={(v) => { setClasificacion(v); setError(""); }}>
                <SelectTrigger data-testid="select-clasificacion" className="mt-1.5 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecciona el resultado…" />
                </SelectTrigger>
                <SelectContent>
                  {RESULTADOS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`clasif-${r.value}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {causaRequerida && !precargado && (
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">{instMode ? "Causa presunta del evento no confirmado" : "Causa presunta"}</label>
              <Select value={causa} onValueChange={(v) => { setCausa(v); setError(""); }}>
                <SelectTrigger data-testid="select-causa" className="mt-1.5 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecciona la causa…" />
                </SelectTrigger>
                <SelectContent>
                  {CAUSAS_FALSA.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`causa-${c.value}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">{tecnicoMode ? "Nota factual (obligatoria)" : `Nota de cierre${notaEfectivaOblig ? " (obligatoria)" : " (opcional)"}`}</label>
            {/* UI-01G · orientación de captura. NO se imponen longitud mínima, bloqueo por
                repetición ni campos estructurados: la autoridad de cierre de la monitorista no
                cambia. Sólo se le dice QUÉ conviene dejar escrito. */}
            <p data-testid="close-nota-ayuda" className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {tecnicoMode
                ? "Escribe qué se revisó, qué se encontró y qué quedó pendiente o resuelto."
                : "Escribe qué ocurrió, cómo lo verificaste y qué gestión realizaste."}
            </p>
            <p data-testid="close-nota-ejemplo" className="mt-0.5 text-xs italic text-slate-400 dark:text-slate-500">
              {tecnicoMode
                ? "Ejemplo: «Se confirmó corte de internet en el sitio; el puente volvió a reportar a las 14:20 y la cobertura quedó restablecida»."
                : "Ejemplo: «Hablé por el dispositivo y la persona respondió que fue una falsa activación; avisé a su hija y quedó al pendiente»."}
            </p>
            {/* ATN-01 · M5 — REUTILIZAR una nota YA registrada del mismo caso (hechos del journal y
                notas de la gestión). Se copia como borrador editable sólo por elección explícita:
                nada se autocopia, la nota original no se modifica y no hay resumen generado. */}
            {notasPrevias.length > 0 && (
              <div data-testid="close-notas-previas" className="mt-2">
                <Select value="" onValueChange={(v) => {
                  const n = notasPrevias.find((x) => String(x.id) === String(v));
                  if (n) { setNota(n.texto); setError(""); }
                }}>
                  <SelectTrigger data-testid="select-nota-previa" className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                    <SelectValue placeholder="Reutilizar una nota ya registrada en este caso…" />
                  </SelectTrigger>
                  <SelectContent>
                    {notasPrevias.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)} data-testid={`nota-previa-${n.id}`}>
                        {n.origen} · {n.actor || "—"} · {n.hora} — {n.texto.slice(0, 60)}{n.texto.length > 60 ? "…" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-400">Se copia como borrador editable. La nota original no se modifica.</p>
              </div>
            )}
            <Textarea
              data-testid="input-nota-cierre"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder={tecnicoMode ? "Qué se revisó, qué se encontró y qué quedó resuelto o pendiente…" : "Qué ocurrió · cómo lo verificaste · qué gestión realizaste…"}
              className="mt-1.5 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              rows={3}
            />
          </div>

          {error && <p data-testid="close-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} className="dark:text-slate-300">
            Cancelar
          </Button>
          <Button
            data-testid="btn-confirmar-cierre"
            onClick={submit}
            disabled={submitting}
            className="bg-[#01CBE3] hover:bg-[#01B5CB] text-[#012472] font-semibold"
          >
            {submitting ? (tecnicoMode && tecSel && !tecSel.terminal ? "Registrando…" : "Cerrando…")
              : tecnicoMode ? (tecSel && !tecSel.terminal ? "Registrar seguimiento" : "Registrar y cerrar") : "Cerrar caso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
